<template>
  <div v-if="currentUser" class="time-sheets-listing">
    <TimeSheetsFilters
      v-if="!hideHeaders"
      :time-sheets-assignments="timeSheetsAssignments"
      :time-sheets-contractors="timeSheetsContractors"
      :time-sheets-filters="timeSheetsFilters"
      :current-user="currentUser"
      @change="onFiltersChange"
    />

    <TolAlert v-if="tolAlert" v-bind="tolAlert" />

    <TimeSheetsActions
      :show-bulk-action="showCheckbox"
      :title-all-or-filtered="allOrFilteredTitle"
    />

    <tol-table
      :table-data="timeSheets"
      :columns="columns"
      :show-spinner="isLoading"
      :is-row-clickable="true"
      @row-click="onRowClick($event)"
    >
      <template v-slot:tableHeader>
        <th v-if="showCheckbox"></th>
        <th v-if="canViewPayrollNumber">{{ $t('timeSheets.listing.labels.payrollNumber') }}</th>

        <th-sortable
          v-if="canViewContractor"
          :direction="currentSortField === 'contractorName' ? currentSortDirection : 'none'"
          field-name="contractorName"
          @change="onSortOrderChange"
          >{{ $t('timeSheets.listing.labels.contractor') }}</th-sortable
        >

        <th-sortable
          :direction="currentSortField === 'clientName' ? currentSortDirection : 'none'"
          field-name="clientName"
          @change="onSortOrderChange"
          >{{ $t('timeSheets.listing.labels.client') }}</th-sortable
        >

        <th-sortable
          :direction="currentSortField === 'startDate' ? currentSortDirection : 'none'"
          field-name="startDate"
          @change="onSortOrderChange"
          >{{ $t('timeSheets.listing.labels.startDate') }}</th-sortable
        >

        <th-sortable
          :direction="currentSortField === 'endDate' ? currentSortDirection : 'none'"
          field-name="endDate"
          @change="onSortOrderChange"
          >{{ $t('timeSheets.listing.labels.endDate') }}</th-sortable
        >

        <th-sortable
          :direction="currentSortField === 'hours' ? currentSortDirection : 'none'"
          field-name="hours"
          @change="onSortOrderChange"
          >{{ $t('timeSheets.listing.labels.totalHours') }}</th-sortable
        >

        <th-sortable
          :direction="currentSortField === 'days' ? currentSortDirection : 'none'"
          field-name="days"
          @change="onSortOrderChange"
          >{{ $t('timeSheets.listing.labels.totalDays') }}</th-sortable
        >

        <th>{{ $t('timeSheets.listing.labels.status') }}</th>
      </template>

      <template v-if="showCheckbox" v-slot:check="{ value }">
        <td @click.stop>
          <Checkbox
            size="small"
            :checked="value.isSelected"
            :modified="value.referenceNumber in modifiedTimeSheets"
            @change="onToggleSelectTimeSheet(value.referenceNumber)"
          />
        </td>
      </template>

      <template v-slot:referenceNumber="{ value }">
        <router-link :to="value.referenceNumber">{{ value.referenceNumber }}</router-link>
      </template>

      <template v-slot:contractor="{ value }">{{ value.contractorName }}</template>

      <template v-slot:startDate="{ value }">
        <font-awesome-icon icon="calendar-alt" />
        {{ value.startDate }}
      </template>

      <template v-slot:endDate="{ value }">
        <font-awesome-icon icon="calendar-alt" />
        {{ value.endDate }}
      </template>

      <template v-slot:status="{ value }">
        <Status :status="value.status" object-type="time-sheet" />
      </template>
    </tol-table>

    <Pagination
      :records-per-page="timeSheetsPerPage"
      :offset="currentPage"
      :record-count="recordCount"
      @change="onChangePaginationParams($event)"
    />
  </div>
</template>

<script>
import { TimeSheetsListingActions } from '@/app/features/time-sheets/features/time-sheets-listing/time-sheets-listing.keys';
import { TimeSheetsListingGetters } from './time-sheets-listing.keys';
import TolAlert from '@/app/components/TolAlert';
import TimeSheetsFilters from '@/app/features/time-sheets/features/time-sheets-listing/components/TimeSheetsFilters';
import TimeSheetsActions from '@/app/features/time-sheets/features/time-sheets-listing/components/TimeSheetsActions';
import { AppActions } from '@/app/app.keys';
import TolTable from '@/app/components/TolTable';
import Status from '@/app/components/Status';
import Checkbox from '@/app/components/Checkbox';
import ThSortable from '@/app/components/ThSortable';
import Pagination from '@/app/components/Pagination';
import { userUtils } from '@/mixins/user-utils';

export default {
  name: 'TimeSheetsListing',
  components: {
    TolAlert,
    TimeSheetsFilters,
    TimeSheetsActions,
    TolTable,
    Status,
    ThSortable,
    Checkbox,
    Pagination
  },
  mixins: [userUtils],
  props: {
    hideHeaders: {
      type: Boolean,
      default: false
    },
    userOnHomePage: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isLoading() {
      return this.$store.getters[TimeSheetsListingGetters.TimeSheetIsLoading];
    },
    timeSheetsPerPage: {
      get() {
        return this.$store.getters.TimeSheetsPerPage;
      }
    },
    currentPage: {
      get() {
        return this.$store.getters.TimeSheetsListingOffset;
      }
    },
    recordCount: {
      get() {
        return +this.$store.getters.TimeSheetsFilteredSortedRecordCount;
      }
    },
    timeSheetsAssignments: function() {
      return this.$store.state.timeSheetsState.timeSheetsListingState.timeSheetsAssignments;
    },

    timeSheetsContractors: function() {
      return this.$store.getters[TimeSheetsListingGetters.TimesheetContractors];
    },

    currentUser: function() {
      return this.$store.state.currentUser;
    },
    canViewPayrollNumber() {
      //only TES employess can create time sheets, and so will see payrol number in listing
      return !!this.currentUser && !!this.currentUser.permissions['TIME_SHEET']['CREATE'];
    },
    canViewContractor() {
      return (
        this.currentUser &&
        this.currentUser.permissions &&
        this.currentUser.permissions.CONTRACTOR &&
        this.currentUser.permissions['CONTRACTOR'] &&
        this.currentUser.permissions['CONTRACTOR']['VIEW_IN_LIST']
      );
    },
    timeSheetsFilters: function() {
      return this.$store.state.timeSheetsState.timeSheetsListingState.timeSheetsFilters;
    },

    timeSheets: function() {
      return this.$store.state.timeSheetsState.timeSheetsListingState.timeSheets;
    },

    timeSheetsStatusCounts() {
      return this.$store.state.timeSheetsState.timeSheetsListingState.timeSheetsStatusCounts;
    },

    tolAlert() {
      if (this.currentUser === null || this.timeSheetsStatusCounts === null) {
        return null;
      }

      if (this.isClientContact && this.timeSheetsStatusCounts.submitted > 0) {
        let count = this.timeSheetsStatusCounts.submitted;
        let status = this.$t('statuses.submitted');
        let message = this.$tc('timeSheets.listing.alertMessage', count, { count, status });
        return { message, type: 'warning' };
      }

      if (this.isContractor && this.timeSheetsStatusCounts.rejected > 0) {
        let count = this.timeSheetsStatusCounts.rejected;
        let status = this.$t('statuses.rejected');
        let message = this.$tc('timeSheets.listing.alertMessage', count, { count, status });
        return { message, type: 'error' };
      }

      return null;
    },
    columns() {
      let tableColumns = ['client', 'startDate', 'endDate', 'totalHours', 'totalDays', 'status'];
      if (this.canViewContractor) tableColumns.splice(0, 0, 'contractor');
      if (this.canViewPayrollNumber) tableColumns.splice(0, 0, 'payrollNumber');
      return tableColumns;
    },
    showCheckbox() {
      let role = this.currentUser.roles.find(
        role =>
          role.role_name == 'Client Contact' ||
          role.role_name == 'TES Approver' ||
          role.role_name === 'System Admin'
      );
      return role ? true : false;
    },
    hasTimeSheets() {
      return this.timeSheets.length > 0;
    },
    currentSortField() {
      return this.$store.state.timeSheetsState.timeSheetsListingState.timeSheetsSortOrder.sortBy;
    },
    currentSortDirection() {
      return this.$store.state.timeSheetsState.timeSheetsListingState.timeSheetsSortOrder
        .sortDirection;
    },
    modifiedTimeSheets() {
      return this.$store.getters.ModifiedTimeSheets;
    },
    allOrFilteredTitle() {
      return this.areAnyFiltersSet(this.timeSheetsFilters)
        ? 'timeSheets.listing.actions.titleFiltered'
        : this.hideHeaders
        ? 'timeSheets.listing.actions.titleYour'
        : 'timeSheets.listing.actions.titleAll';
    },
    canApprove() {
      return (
        this.currentUser &&
        this.currentUser.permissions &&
        this.currentUser.permissions.TIME_SHEET &&
        this.currentUser.permissions['TIME_SHEET']['APPROVE']
      );
    },
    canReject() {
      return (
        this.currentUser &&
        this.currentUser.permissions &&
        this.currentUser.permissions.TIME_SHEET &&
        this.currentUser.permissions['TIME_SHEET']['REJECT']
      );
    },
    canProcess() {
      return (
        this.currentUser &&
        this.currentUser.permissions &&
        this.currentUser.permissions.TIME_SHEET &&
        this.currentUser.permissions['TIME_SHEET']['PROCESS']
      );
    }
  },
  mounted: function() {
    if (this.userOnHomePage) {
      this.$store.dispatch(AppActions.SetPageTitle, this.$t('app.homePage.title'));
    } else {
      this.$store.dispatch(AppActions.SetPageTitle, this.$t('timeSheets.listing.title'));
    }
    this.$store.dispatch(TimeSheetsListingActions.RetrieveTimeSheetsStatusCounts);
    this.$store.dispatch(TimeSheetsListingActions.RetrieveTimeSheets);
  },
  methods: {
    areAnyFiltersSet(timeSheetsFilters) {
      return (
        timeSheetsFilters.assignmentId ||
        timeSheetsFilters.contractorId ||
        timeSheetsFilters.dateRangeFilters.startDate ||
        timeSheetsFilters.dateRangeFilters.endDate ||
        Object.values(timeSheetsFilters.statusFilters).some(val => val)
      );
    },
    onFiltersChange(timeSheetsFilters) {
      this.$store.dispatch(TimeSheetsListingActions.SetTimeSheetsFilters, timeSheetsFilters);
    },
    onSortOrderChange(sortOrder) {
      this.$store.dispatch(TimeSheetsListingActions.SetTimeSheetsSortOrder, sortOrder);
    },
    onToggleSelectTimeSheet(event) {
      //Add selected timesheet to selectedTimesheets in store
      this.$store.dispatch(TimeSheetsListingActions.SelectTimeSheet, event);
    },
    onChangePaginationParams(event) {
      this.$store.dispatch(TimeSheetsListingActions.SetTimeSheetsPaginationParameters, event);
    },
    onRowClick(evt) {
      this.$router.push(`/time-sheets/${evt.referenceNumber}`);
    }
  }
};
</script>
<style scoped lang="scss"></style>
